<template>
  <ClientOnly v-if="displayBanner && !!bannerText">
    <Transition
      appear
      appear-active-class="transform transition-all duration-[300ms] ease-[cubic-bezier(0,0,0.4,0)]"
      appear-from-class="opacity-0 -translate-y-48"
      appear-to-class="opacity-100 translate-y-0"
    >
      <aside
        v-if="bannerOpen"
        class="flex h-48 w-full items-center justify-between pr-8 overflow-x-hidden relative"
        :class="{
          'bg-surface-default-low dark:bg-action-default-mid dark:mood-blush text-static-default-hi mood-inverse':
            variant === 'promotional',
          'bg-static-danger-hi text-action-default-hi': variant === 'emergency',
        }"
        data-test="promotional-banner"
      >
        <div
          ref="marqueeBoundary"
          class="flex-1 overflow-hidden"
          :class="{ 'gradient-mask-r-90 pl-64': marqueeTextNeedsAnimation }"
        >
          <div
            ref="animationBoundary"
            :class="[
              marqueeTextNeedsAnimation
                ? `relative flex w-max gap-64 ${$style.marquee}`
                : 'flex justify-center',
            ]"
            role="presentation"
            @click.passive="changeAnimationState('paused')"
            @focusin.passive="changeAnimationState('paused')"
            @focusout.passive="changeAnimationState('running')"
            @keyup.passive="changeAnimationState('running')"
            @mouseout.passive="changeAnimationState('running')"
            @mouseover.passive="changeAnimationState('paused')"
          >
            <div ref="textBoundary" class="flex whitespace-nowrap">
              <RichText
                class="body-1"
                :node-renderers-overrides="noHeadingsOrEmptyParagraphs"
                :rich-text="bannerText.props.richText"
                style-variant="none"
                :tracking="{ ...trackingInfo, action: 'bannerTextLinkClick' }"
              />
            </div>

            <RichText
              class="body-1"
              :class="[marqueeTextNeedsAnimation ? 'block' : 'hidden']"
              :node-renderers-overrides="noHeadingsOrEmptyParagraphs"
              :rich-text="bannerText.props.richText"
              style-variant="none"
              :tracking="{ ...trackingInfo, action: 'bannerTextLinkClick' }"
            />
          </div>
        </div>

        <div
          class="absolute h-full top-0 left-1/2 -translate-x-1/2 -ml-20 min-w-300"
          :class="[$style.shimmer, { [$style.animateShimmer]: readyToShimmer }]"
          :style="`width: ${marqueeTextWidth}px`"
        />

        <RevButtonIcon
          :aria-label="i18n(translations.alternativeClose)"
          :icon="IconCrossSmall"
          size="small"
          variant="ghost"
          @click="onBannerClose"
        />
      </aside>
    </Transition>

    <template v-if="!!bannerText" #fallback>
      <div
        class="h-48 w-full bg-action-default-min"
        data-test="promotional-banner-skeleton"
      />
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import type { _ApiBlock } from '@backmarket/http-api/src/api-specs-content/models/block'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isClient } from '@backmarket/nuxt-module-tracking/isClient'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import RichText from '../../blocks/RichText/RichText.vue'
import translations from '../../common/CMS.translations'

import { useFetchPromotionalBanner } from './useFetchPromotionalBanner'

const { displayOnPage = true } = defineProps<{ displayOnPage?: boolean }>()

const bannerOpen = ref(true)
const experiments = useExperiments()
const route = useRoute()
const { trackClick } = useTracking()
const i18n = useI18n()

// pixels per second
const MARQUEE_SCROLL_SPEED = 50

const { data, execute } = useFetchPromotionalBanner()

// in case both the PromotionalBanner & Fallback calls fail during SSR
// we want to avoid re-triggering useAsyncData() on the client as we already have
// the 'page:finish' hook. useAsyncData() doesn't keep null/undefined responses cached.
if (!isClient()) {
  await execute()
}

const marqueeBoundary = useTemplateRef<HTMLDivElement>('marqueeBoundary')
const textBoundary = useTemplateRef<HTMLDivElement>('textBoundary')
const animationBoundary = useTemplateRef<HTMLDivElement>('animationBoundary')
const marqueeTextNeedsAnimation = ref<boolean>(false)
const marqueeScrollDuration = ref<number>()
const readyToShimmer = ref(false)
const marqueeTextWidth = ref(0)

const variant = computed(() => {
  if (!data?.value) return 'promotional'

  const { blockList3: emergencyText } = data.value

  return emergencyText?.length ? 'emergency' : 'promotional'
})

// promotional banners are to be shown only on certain pages
// emergency banners can be shown everywhere
const displayBanner = computed(() => {
  if (variant.value === 'emergency') return true

  return variant.value === 'promotional' && displayOnPage
})

const textType = computed<'control' | 'variant' | 'emergency' | undefined>(
  () => {
    if (!data?.value) return undefined

    /**
     * Cases:
     * - we have emergency text -> it takes priority, return it
     * - we DO NOT have control text -> hide banner
     * - we have control & variant text, experiment or boolean are OFF -> display control banner
     * - we have control & NO variant text, experiment or boolean are OFF -> display control banner
     * - we have control & NO variant text, experiment AND boolean are ON -> hide banner (ie: B test is with the banner hidden)
     * - we have control & variant text, experiment AND boolean are ON -> display variant OR control according to experiment
     * - we have control text -> display control banner
     */

    const {
      blockList1: controlText,
      blockList2: variantText,
      blockList3: emergencyText,
      testEnabled,
    } = data.value

    const hasEmergencyText = emergencyText?.length
    if (hasEmergencyText) return 'emergency'

    const hasControlText = controlText?.length
    if (!hasControlText) return undefined

    const hasVariantText = variantText?.length
    const variantExperimentEnabled =
      testEnabled &&
      experiments['experiment.cmsPromotionalBanner'] === 'variantText'

    if (variantExperimentEnabled && !hasVariantText) return undefined
    if (variantExperimentEnabled && hasVariantText) return 'variant'

    return 'control'
  },
)

// The V1 of this feature accepts a list of RichText elements but will only have 1,
// in the future it will be a "carousel" type of message display.
const bannerText = computed<_ApiBlock<'RichText'> | undefined>(() => {
  if (!data?.value || !textType.value) return undefined

  const {
    blockList1: controlText,
    blockList2: variantText,
    blockList3: emergencyText,
  } = data.value

  switch (textType.value) {
    case 'control':
      return controlText?.[0]
    case 'variant':
      return variantText?.[0]
    case 'emergency':
      return emergencyText?.[0]
    default:
      return undefined
  }
})

const trackingInfo = computed(() => {
  const info = {
    sourceUrl: route.fullPath,
    pageType: route.name?.toString(),
    pageName: route.params?.pageName?.toString(),
    zone:
      variant.value === 'promotional' ? 'promotionalBanner' : 'emergencyBanner',
    textType: textType.value ?? '',
  }

  return removeEmptyValuesInObject(info)
})

function onBannerClose() {
  bannerOpen.value = false

  trackClick({ ...trackingInfo.value, action: 'bannerClose' })
}

// don't render any H tags by replacing them with <p> & hide empty paragraphs on all
const noHeadingsOrEmptyParagraphs: NodeRendererMap = {
  'heading-1': (node, key, next) => {
    return h(
      'p',
      { key, class: tw`empty:hidden` },
      next(node.content, key, next),
    )
  },
  'heading-2': (node, key, next) => {
    return h('p', { class: tw`empty:hidden` }, next(node.content, key, next))
  },
  'heading-3': (node, key, next) => {
    return h('p', { class: tw`empty:hidden` }, next(node.content, key, next))
  },
  'heading-4': (node, key, next) => {
    return h('p', { class: tw`empty:hidden` }, next(node.content, key, next))
  },
  'heading-5': (node, key, next) => {
    return h('p', { class: tw`empty:hidden` }, next(node.content, key, next))
  },
  'heading-6': (node, key, next) => {
    return h('p', { class: tw`empty:hidden` }, next(node.content, key, next))
  },
  paragraph: (node, key, next) => {
    return h(
      'p',
      { key, class: tw`empty:hidden` },
      next(node.content, key, next),
    )
  },
}

async function initMarquee() {
  if (!marqueeBoundary.value || !textBoundary.value) return

  await nextTick()

  const { clientWidth: boundaryWidth } = marqueeBoundary.value
  const { clientWidth: textWidth } = textBoundary.value

  marqueeTextWidth.value = textWidth

  marqueeScrollDuration.value = textWidth / MARQUEE_SCROLL_SPEED
  marqueeTextNeedsAnimation.value = textWidth > boundaryWidth
}

watch([marqueeBoundary, textBoundary, bannerText], async () => {
  if (!import.meta.client) return

  void initMarquee()
})

function changeAnimationState(state: 'running' | 'paused') {
  if (!animationBoundary.value) return

  animationBoundary.value.style.animationPlayState = state
}

const handleAnimationScrollState = () => changeAnimationState('running')

onMounted(() => {
  void initMarquee()
  window.addEventListener('resize', initMarquee, { passive: true })

  window.addEventListener('scroll', handleAnimationScrollState, {
    passive: true,
  })

  readyToShimmer.value = true
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', initMarquee)
  window.removeEventListener('scroll', handleAnimationScrollState)
})
</script>

<style module>
.marquee {
  animation: marquee calc(v-bind(marqueeScrollDuration) * 1s) linear infinite;
}

@keyframes marquee {
  to {
    /* 32px offset is there for the 64px gap between each text
     -> avoids stuttering during transition */
    transform: translateX(calc(-50% - 32px));
  }
}

@keyframes animateShimmer {
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateX(30%);
  }
}

.shimmer {
  opacity: 0;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    rgba(0, 0, 0, 0.8) 15%,
    rgba(0, 0, 0, 0.8) 85%,
    transparent 100%
  );

  background: linear-gradient(
    -20deg,
    transparent 35%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 85%
  );

  @media (prefers-color-scheme: dark) {
    background: linear-gradient(
      -20deg,
      transparent 35%,
      rgba(255, 255, 255, 0.7) 50%,
      transparent 85%
    );
  }
}

.animateShimmer {
  animation: animateShimmer 1600ms linear;

  @media (min-width: theme('screens.md')) {
    animation: animateShimmer 1800ms linear;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  animation-delay: 2s;
}
</style>
